import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { stashReturnPath } from "@/lib/auth-return-path";

interface ProtectedRouteProps {
  children: ReactNode;
  requireAdmin?: boolean;
}

export const ProtectedRoute = ({
  children,
  requireAdmin = false,
}: ProtectedRouteProps) => {
  const { user, isAdmin, loading, rolesReady } = useAuth();
  const location = useLocation();

  // Roles load asynchronously after the session resolves. On a hard load of a
  // deep /admin route (e.g. "open in new tab"), keep showing the spinner until
  // roles are known instead of bouncing an admin to /dashboard (COL-255).
  if (loading || (requireAdmin && user && !rolesReady)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    // Remember where they were headed before the bounce. Tenants reach deep
    // links like /dashboard?tab=checkin straight from the move-in/move-out
    // emails, usually with an expired session; without this the destination was
    // lost here and signing in dropped them on the dashboard Home tab.
    // Written during render rather than in an effect: <Navigate> unmounts this
    // component on its own effect, so a stash effect here would never run.
    stashReturnPath(`${location.pathname}${location.search}${location.hash}`);
    return <Navigate to="/" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};
